import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { LscPublishConfirm } from '../types';

interface PublishConfirmModalProps {
  open: boolean;
  title?: string;
  confirm?: LscPublishConfirm | null;
  onClose: () => void;
  onConfirm: (visibility: string, note: string) => Promise<void>;
  publishing: boolean;
}

export function PublishConfirmFacts({ confirm }: { confirm?: LscPublishConfirm | null }) {
  if (!confirm) return null;
  const teams = (confirm.teams_read ?? []).filter(Boolean);
  return (
    <div className="text-sm space-y-1 mb-4" data-testid="publish-confirm-facts">
      {confirm.gamertag_warning ? (
        <p
          className="rounded-md border border-amber-400 bg-amber-950 px-3 py-2 text-amber-50"
          data-testid="publish-confirm-gamertag-warning"
        >
          {confirm.gamertag_warning}
        </p>
      ) : null}
      {confirm.matchup ? (
        <p className="text-white" data-testid="publish-confirm-matchup">
          {confirm.matchup}
        </p>
      ) : null}
      {confirm.when_label ? (
        <p className="text-secondary-300" data-testid="publish-confirm-when">
          {confirm.when_label}
        </p>
      ) : null}
      {confirm.broadcaster ? (
        <p className="text-secondary-300" data-testid="publish-confirm-broadcaster">
          Broadcaster: {confirm.broadcaster}
        </p>
      ) : null}
      {teams.length > 0 ? (
        <p className="text-secondary-300" data-testid="publish-confirm-teams">
          Scoreboard read: {teams.join(', ')}
        </p>
      ) : null}
      {confirm.broadcaster_warning ? (
        <p className="text-amber-200" data-testid="publish-confirm-broadcaster-warning">
          {confirm.broadcaster_warning}
        </p>
      ) : null}
      {confirm.frame_confirmation === 'not_confirmed' ? (
        <p className="text-amber-200" data-testid="publish-confirm-frame-warning">
          The replay frames don't confirm this matchup. Publishing anyway.
        </p>
      ) : null}
    </div>
  );
}

const PublishConfirmModal: React.FC<PublishConfirmModalProps> = ({
  open,
  title,
  confirm,
  onClose,
  onConfirm,
  publishing,
}) => {
  const [visibility, setVisibility] = useState('league_members');
  const [note, setNote] = useState('');

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onConfirm(visibility, note);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="publish-modal-title"
      data-testid="publish-confirm-modal"
    >
      <div className="card max-w-md w-full relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-secondary-400 hover:text-white"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 id="publish-modal-title" className="text-lg font-semibold text-white mb-2">
          Publish to archive
        </h2>
        <p className="text-sm text-secondary-400 mb-4">
          {title
            ? `Publish "${title}" for league members to watch.`
            : 'Publish this approved VOD to the league archive.'}
        </p>
        <PublishConfirmFacts confirm={confirm} />
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Visibility</span>
            <select
              className="input-field"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              disabled={publishing}
            >
              <option value="league_members">League members</option>
              <option value="commissioner_only">Commissioner only</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Note (optional)</span>
            <textarea
              className="input-field min-h-[80px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={publishing}
              placeholder="Optional commissioner note"
            />
          </label>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={publishing}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={publishing}>
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublishConfirmModal;
